import { useMemo } from 'react';
import { SourcesStatsWithShare, SourceStatsWithShare } from './UseSourcesStatsShare';

export type SourceStatsWithComparison = SourceStatsWithShare & {
  previousShare: number;
  previousPromptCount: number;
  shareChange: number;
  promptCountChange: number;
};

export type SourcesStatsWithComparison = {
  sources: SourceStatsWithComparison[];
  pagination?: SourcesStatsWithShare['pagination'];
  totalCount?: number;
};

/**
 * Hook to compare sources stats between two periods
 * Calculates share changes and prompt count changes (absolute numeric difference) between current and previous period
 * Requires shares to be pre-calculated using useSourcesStatsShare
 * Returns only shareChange and promptCountChange, not share (share comes from share hooks)
 */
export const useSourceStatsComparison = (
  currentStatsWithShare: SourcesStatsWithShare | undefined,
  previousStatsWithShare: SourcesStatsWithShare | undefined
): SourcesStatsWithComparison | undefined => {
  return useMemo(() => {
    if (!currentStatsWithShare?.sources) {
      return currentStatsWithShare ? {
        sources: [],
        pagination: currentStatsWithShare.pagination,
        totalCount: currentStatsWithShare.totalCount,
      } : undefined;
    }

    // Create a map of previous period data for fast lookup
    const previousStatsMap = new Map(
      previousStatsWithShare?.sources?.map(source => [source.source, source]) || []
    );

    // Process sources with share changes
    const enrichedSources: SourceStatsWithComparison[] = currentStatsWithShare.sources.map(currentSource => {
      const currentSourceShare = currentSource.share;

      const previousSource = previousStatsMap.get(currentSource.source);
      const previousSourceShare = previousSource?.share || 0;

      const sourceShareChange = currentSourceShare - previousSourceShare;

      // Calculate prompt count change (absolute numeric difference)
      const currentPromptCount = currentSource.promptCount || 0;
      const previousPromptCount = previousSource?.promptCount || 0;
      const promptCountChange = currentPromptCount - previousPromptCount;

      return {
        ...currentSource,
        previousShare: previousSourceShare,
        previousPromptCount,
        shareChange: sourceShareChange,
        promptCountChange,
      };
    });

    return {
      sources: enrichedSources,
      pagination: currentStatsWithShare.pagination,
      totalCount: currentStatsWithShare.totalCount,
    };
  }, [currentStatsWithShare, previousStatsWithShare]);
};

